<template>
  <div class="summary-report-tab">
    <!-- Filters Section -->
    <b-row>
      <b-col cols="12">
        <b-card class="filter-card compact">
          <b-card-body>
            <b-row class="align-items-end">
              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Reviewer Name" label-size="sm">
                  <v-select
                    v-model="reviewerNameFilter"
                    :options="uniqueReviewers"
                    :placeholder="
                      uniqueReviewers.length > 0
                        ? 'All reviewers...'
                        : 'No reviewers available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueReviewers.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Reviewee Name" label-size="sm">
                  <v-select
                    v-model="revieweeNameFilter"
                    :options="uniqueReviewees"
                    :placeholder="
                      uniqueReviewees.length > 0
                        ? 'All reviewees...'
                        : 'No reviewees available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueReviewees.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Section Name" label-size="sm">
                  <v-select
                    v-model="groupNameFilter"
                    :options="uniqueGroups"
                    :placeholder="
                      uniqueGroups.length > 0
                        ? 'All sections...'
                        : 'No sections available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueGroups.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Question Name" label-size="sm">
                  <v-select
                    v-model="questionNameFilter"
                    :options="uniqueQuestions"
                    :placeholder="
                      uniqueQuestions.length > 0
                        ? 'All questions...'
                        : 'No questions available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueQuestions.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Status" label-size="sm">
                  <v-select
                    v-model="statusFilter"
                    :options="uniqueStatuses"
                    :placeholder="
                      uniqueStatuses.length > 0
                        ? 'All statuses...'
                        : 'No statuses available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueStatuses.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
                <b-form-group label="Test Taken Count" label-size="sm">
                  <v-select
                    v-model="testTakenCountFilter"
                    :options="uniqueTestCounts"
                    :placeholder="
                      uniqueTestCounts.length > 0
                        ? 'All counts...'
                        : 'No counts available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueTestCounts.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col
                xl="3"
                lg="3"
                md="6"
                sm="6"
                cols="12"
                class="mb-1"
                :class="{ 'd-none': !hasActiveFilters }"
              >
                <b-form-group>
                  <b-button
                    @click="clearAllFilters"
                    variant="outline-danger"
                    size="sm"
                    v-b-tooltip.hover
                    title="Clear all filters"
                    class="d-flex align-items-end justify-content-end"
                  >
                    <feather-icon icon="XIcon" size="14" class="mr-1" />
                    Clear All Filters
                  </b-button>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Results Summary -->
    <b-row class="mb-0" v-if="resultData.data.length > 0">
      <b-col cols="12">
        <small class="text-muted">
          Showing {{ resultData.data.length }} group(s) with
          {{ totalQuestions }} question(s) ({{ totalReviews }}
          total reviews)
          <span v-if="hasActiveFilters" class="text-info ml-2">
            <feather-icon icon="FilterIcon" size="12" class="mr-1" />
            {{ activeFiltersCount }} filter(s) applied
          </span>
        </small>
      </b-col>
    </b-row>

    <!-- Table Section -->
    <b-row class="mt-1">
      <b-col cols="12" v-if="resultData.data.length > 0">
        <b-overlay :show="tableShow" rounded="true" :fixed="true" no-fade>
          <div
            class="compact-table-container animate__animated animate__fadeIn"
          >
            <!-- No results message -->
            <div v-if="filteredResults.length === 0" class="text-center py-5">
              <feather-icon
                icon="AlertCircleIcon"
                size="48"
                class="text-muted mb-2"
              />
              <h5 class="text-muted">No results found</h5>
              <p class="text-muted">
                Try adjusting your filters or check if data is available.
              </p>
            </div>

            <!-- Compact Results Table -->
            <div v-else class="results-table-wrapper">
              <div
                class="table-responsive horizontal-scroll"
                style="overflow-x: auto; width: 100%;"
              >
                <b-table
                  :items="paginatedResults"
                  :fields="tableFields"
                  striped
                  hover
                  small
                  bordered
                  :fixed="false"
                  class="mb-0 compact-results-table text-break"
                  table-class="word-break-table"
                  show-empty
                  empty-text="No review results found"
                  :tbody-tr-class="getRowClass"
                  style="table-layout: auto !important; word-wrap: break-word !important;"
                  :tbody-tr-attr="getRowAttributes"
                >
                  <!-- Reviewer Name -->
                  <template #cell(reviewer_name)="data">
                    <span
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                    >
                      {{ data.item.reviewer_name }}
                    </span>
                  </template>

                  <!-- Reviewee Name -->
                  <template #cell(reviewee_name)="data">
                    <span
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                    >
                      {{ data.item.reviewee_name }}
                    </span>
                  </template>

                  <!-- Group/Section Name -->
                  <template #cell(group_name)="data">
                    <span
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                    >
                      {{ data.item.group_name }}
                    </span>
                  </template>

                  <!-- Question Order/Number -->
                  <template #cell(question_order)="data">
                    <span
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                    >
                      {{ data.item.question_order }}
                    </span>
                  </template>

                  <!-- Section Score -->
                  <template #cell(section_score)="data">
                    <span
                      v-if="data.item.section_score"
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                    >
                      {{ data.item.section_score }}
                    </span>
                  </template>

                  <!-- Compulsory -->
                  <template #cell(compulsory)="data">
                    <span
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                    >
                      {{ data.item.compulsory }}
                    </span>
                  </template>

                  <!-- Min Score of Quest -->
                  <template #cell(min_score_of_quest)="data">
                    <span
                      :class="{
                        'failed-cell':
                          shouldApplyFailedStyle(data.item) &&
                          !isCommentQuestion(data.item),
                        'comment-indicator': isCommentQuestion(data.item),
                      }"
                    >
                      {{ getMinScoreDisplay(data.item) }}
                    </span>
                  </template>

                  <!-- Question Name with truncation -->
                  <template #cell(question_name)="data">
                    <div
                      class="question-name-cell"
                      :class="{
                        'group-header': data.item.rowType === 'group',
                        'question-row': data.item.rowType === 'question',
                        'failed-row text-danger': data.item.status === 'Failed',
                      }"
                    >
                      <span
                        :title="data.item.question_name"
                        v-b-tooltip.hover
                        :class="{
                          'question-text': true,
                          'group-text font-weight-bold':
                            data.item.rowType === 'group',
                          'question-indent': data.item.rowType === 'question',
                        }"
                        style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important; display: block !important;"
                      >
                        {{ data.item.question_name }}
                      </span>
                    </div>
                  </template>

                  <!-- Score with badge formatting like Status -->
                  <template #cell(reviewee_score)="data">
                    <div
                      v-if="
                        data.item.reviewee_score !== '' &&
                          data.item.reviewee_score !== null
                      "
                      class="score-badges"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important;"
                    >
                      <b-badge
                        :variant="
                          getStatusVariant(
                            data.item.status,
                            data.item.max_score_of_question,
                            data.item.question_name,
                            data.item.min_score_of_quest
                          )
                        "
                        :class="{
                          'score-badge': true,
                          'group-score': data.item.rowType === 'group',
                        }"
                      >
                        {{ data.item.reviewee_score || 0 }}
                      </b-badge>
                    </div>
                  </template>

                  <!-- Max Score -->
                  <template #cell(max_score_of_question)="data">
                    <span
                      v-if="
                        data.item.max_score_of_question !== '' &&
                          data.item.max_score_of_question !== null
                      "
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important; display: inline-block !important;"
                    >
                      {{ data.item.max_score_of_question }}
                    </span>
                  </template>

                  <!-- Passing Score -->
                  <template #cell(passing_score)="data">
                    <span
                      v-if="
                        data.item.passing_score !== '' &&
                          data.item.passing_score !== null
                      "
                      class="passing-score-badge"
                      :class="{
                        'font-weight-bold': data.item.rowType === 'group',
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important; display: inline-block !important;"
                    >
                      {{ data.item.passing_score }}
                    </span>
                  </template>

                  <!-- Status with badge -->
                  <template #cell(status)="data">
                    <div
                      v-if="data.item.status"
                      class="status-badges"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important;"
                    >
                      <b-badge
                        :variant="
                          getStatusVariant(
                            data.item.status,
                            data.item.max_score_of_question,
                            data.item.question_name,
                            data.item.min_score_of_quest
                          )
                        "
                        :class="{
                          'status-badge': true,
                          'group-status': data.item.rowType === 'group',
                        }"
                      >
                        {{
                          getSimplifiedStatus(
                            data.item.status,
                            data.item.max_score_of_question,
                            data.item.question_name,
                            data.item.min_score_of_quest
                          )
                        }}
                      </b-badge>
                    </div>
                  </template>

                  <!-- Success Rate with percentage progress bar -->
                  <template #cell(success_rate_of_question)="data">
                    <div
                      v-if="data.item.success_rate_of_question"
                      class="success-rate-cell"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important;"
                    >
                      <div class="success-rate-progress-container">
                        <div
                          class="success-rate-progress-bar"
                          :style="{
                            width:
                              getSuccessRatePercentage(
                                data.item.success_rate_of_question
                              ) + '%',
                            backgroundColor: getSuccessRateColor(
                              data.item.success_rate_of_question,
                              data.item
                            ),
                          }"
                        ></div>
                        <span
                          class="success-rate-text"
                          :class="{
                            'failed-cell': shouldApplyFailedStyle(data.item),
                          }"
                          >{{ data.item.success_rate_of_question }}</span
                        >
                      </div>
                    </div>
                  </template>

                  <!-- Test Taken Count -->
                  <template #cell(test_taken_count)="data">
                    <b-badge
                      v-if="data.item.test_taken_count"
                      :variant="
                        data.item.status === 'Failed'
                          ? 'danger'
                          : 'light-primary'
                      "
                      class="test-count-badge"
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important;"
                    >
                      {{ data.item.test_taken_count || 0 }}
                    </b-badge>
                  </template>

                  <!-- Comment -->
                  <template #cell(comment)="data">
                    <span
                      v-if="data.item.comment"
                      :title="data.item.comment"
                      class="comment-text"
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important; display: block !important;"
                    >
                      {{ data.item.comment }}
                    </span>
                  </template>

                  <!-- Event Name -->
                  <template #cell(event_name)="data">
                    <div
                      v-if="data.item.event_name"
                      :title="data.item.event_name"
                      class="event-name-text"
                      :class="{
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important;"
                    >
                      {{ data.item.event_name }}
                    </div>
                  </template>

                  <!-- Completion Time/Date -->
                  <template #cell(completion_time)="data">
                    <span
                      class="completion-date"
                      :class="{
                        'text-muted':
                          data.item.completion_time === 'Not Completed' &&
                          data.item.status !== 'Failed',
                        'text-success':
                          data.item.completion_time !== 'Not Completed' &&
                          data.item.status !== 'Failed',
                        'failed-cell': shouldApplyFailedStyle(data.item),
                      }"
                      style="min-width: 100px !important; max-width: 400px !important; width: auto !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; word-break: normal !important; display: inline-block !important;"
                    >
                      {{ data.item.completion_time }}
                    </span>
                  </template>
                </b-table>
              </div>

              <!-- Pagination -->
              <div
                class="d-flex justify-content-between align-items-center mt-3"
              >
                <div>
                  <small class="text-muted">
                    Showing {{ startIndex + 1 }}-{{ endIndex }} of
                    {{ filteredResults.length }} items
                  </small>
                </div>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="filteredResults.length"
                  :per-page="perPage"
                  :hide-goto-end-buttons="false"
                  :prev-text="'Previous'"
                  :next-text="'Next'"
                  :first-text="'First'"
                  :last-text="'Last'"
                  size="sm"
                  class="mb-0"
                />
                <div>
                  <b-form-group class="mb-0 d-flex align-items-center">
                    <label class="mr-2 mb-0 small">Items per page:</label>
                    <b-form-select
                      v-model="perPage"
                      :options="[10, 25, 50, 100]"
                      size="sm"
                      style="width: auto;"
                      @change="currentPage = 1"
                    />
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </b-col>

      <b-col cols="12" v-else>
        <div class="text-center py-5">
          <feather-icon
            icon="AlertCircleIcon"
            size="64"
            class="text-muted mb-3"
          />
          <h3 class="text-muted">No Results Available</h3>
          <p class="text-muted">
            There are no results for this record. Please try a different
            selection.
          </p>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BBadge,
  BTable,
  BOverlay,
  BPagination,
  BFormSelect,
} from "bootstrap-vue";
import vSelect from "vue-select";
import moment from "moment";

export default {
  name: "SummaryReportTab",
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BBadge,
    BTable,
    BOverlay,
    BPagination,
    BFormSelect,
    vSelect,
  },
  props: {
    resultData: {
      type: Object,
      default: () => ({
        data: [],
        total_records: 0,
        total_reviews: 0,
      }),
    },
    filterOptions: {
      type: Object,
      default: () => ({
        reviewers: [],
        reviewees: [],
        sections: [],
        statuses: [],
        test_taken_counts: [],
      }),
    },
    tableShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Search filters
      reviewerNameFilter: "",
      revieweeNameFilter: "",
      groupNameFilter: "",
      questionNameFilter: "",
      statusFilter: "",
      testTakenCountFilter: "",

      // Pagination
      currentPage: 1,
      perPage: 25,

      // Table fields for compact display
      tableFields: [
        {
          key: "reviewer_name",
          label: "Reviewer Name",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "reviewee_name",
          label: "Reviewee Name",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "group_name",
          label: "Section Name",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "passing_score",
          label: "Section Min Score",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "question_order",
          label: "Question Number",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "question_name",
          label: "Question Name",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "max_score_of_question",
          label: "Max Score of Question",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "reviewee_score",
          label: "Reviews Score",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "comment",
          label: "Comment",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "section_score",
          label: "Section Score",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "compulsory",
          label: "Compulsory",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "event_name",
          label: "Event Name",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "status",
          label: "Status",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "success_rate_of_question",
          label: "Success Rate of Question",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "completion_time",
          label: "Completion Time",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "test_taken_count",
          label: "Test Taken Count",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
        {
          key: "min_score_of_quest",
          label: "Min Score Of Question",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            fontSize: "11px",
            padding: "4px 6px",
          },
        },
      ],
    };
  },
  computed: {
    flattenedTableData() {
      const flattened = [];

      if (!this.resultData.data || this.resultData.data.length === 0) {
        return [];
      }

      // Track test sequence numbers per reviewee
      const revieweeTestCounts = {};

      // First, sort data by completion time to get chronological order
      const sortedData = [...this.resultData.data].sort((a, b) => {
        const dateA = new Date(a.completion_time || 0);
        const dateB = new Date(b.completion_time || 0);
        return dateA - dateB;
      });

      sortedData.forEach((group, groupIndex) => {
        // Calculate test sequence number for this reviewee
        const revieweeName = group.reviewee_name;
        const testId =
          group.id ||
          group.survey_id ||
          `${group.reviewer_name}_${group.reviewee_name}_${group.event_name}_${group.completion_time}`;

        if (!revieweeTestCounts[revieweeName]) {
          revieweeTestCounts[revieweeName] = new Set();
        }

        // Only increment if this is a new unique test for this reviewee
        if (!revieweeTestCounts[revieweeName].has(testId)) {
          revieweeTestCounts[revieweeName].add(testId);
        }

        // Get current test number for this reviewee
        const currentTestNumber = revieweeTestCounts[revieweeName].size;

        // Calculate correct group status based on questions
        let groupStatus = "Passed";
        if (group.questions && group.questions.length > 0) {
          // If any question is Failed, the entire group should be Failed
          const hasFailedQuestion = group.questions.some(
            (question) => question.status === "Failed"
          );
          if (hasFailedQuestion) {
            groupStatus = "Failed";
          }
        }

        // Group header row'unu kaldırdık - sadece question row'ları gösterilecek

        // Add individual question rows
        if (group.questions && group.questions.length > 0) {
          group.questions.forEach((question, questionIndex) => {
            const questionRow = {
              id: `question-${groupIndex}-${questionIndex}`,
              rowType: "question",
              reviewer_name: group.reviewer_name,
              reviewee_name: group.reviewee_name,
              group_name: group.group_name,
              passing_score: group.passing_score_group,
              question_order: question.order || questionIndex + 1,
              question_name: question.question_name,
              max_score_of_question: question.max_score_of_question,
              reviewee_score: question.reviewee_score,
              comment: question.comment || "",
              section_score: group.questions_scores_summed,
              compulsory: question.compulsory || "No",
              event_name:
                group.file_name ||
                group.event_name ||
                "Short Term Advocacy Quality Review",
              status: question.status,
              success_rate_of_question: question.success_rate_of_question,
              completion_time: this.formatCompletionTime(group.completion_time),
              test_taken_count: currentTestNumber, // Use sequence number instead of total count
              min_score_of_quest:
                question.min_score_of_question || question.passing_score,
              _rowVariant: "question-row",
            };

            flattened.push(questionRow);
          });
        }
      });

      return flattened;
    },

    filteredResults() {
      let filtered = this.flattenedTableData;

      if (this.reviewerNameFilter) {
        filtered = filtered.filter(
          (item) =>
            item.reviewer_name &&
            item.reviewer_name
              .toLowerCase()
              .includes(this.reviewerNameFilter.toLowerCase())
        );
      }

      if (this.revieweeNameFilter) {
        filtered = filtered.filter(
          (item) =>
            item.reviewee_name &&
            item.reviewee_name
              .toLowerCase()
              .includes(this.revieweeNameFilter.toLowerCase())
        );
      }

      if (this.groupNameFilter) {
        filtered = filtered.filter(
          (item) =>
            item.group_name &&
            item.group_name
              .toLowerCase()
              .includes(this.groupNameFilter.toLowerCase())
        );
      }

      if (this.questionNameFilter) {
        filtered = filtered.filter(
          (item) =>
            item.question_name &&
            item.question_name.toLowerCase() ===
              this.questionNameFilter.toLowerCase()
        );
      }

      if (this.statusFilter) {
        filtered = filtered.filter((item) => {
          if (!item.status) return false;

          // Get the simplified status to match filter logic
          const simplifiedStatus = this.getSimplifiedStatus(
            item.status,
            item.max_score_of_question,
            item.question_name
          );

          // If user selected "Passed", show all items that simplify to "Passed" + comment questions
          if (this.statusFilter === "Passed") {
            // Show if simplified status is Passed OR if it's a comment question
            const isCommentQuestion =
              item.question_name &&
              item.question_name.toLowerCase().includes("comment");
            const hasZeroMaxScore =
              item.max_score_of_question === 0 ||
              item.max_score_of_question === "0" ||
              parseInt(item.max_score_of_question) === 0;
            const isStatusB = item.status === "B";

            return (
              simplifiedStatus === "Passed" ||
              isCommentQuestion ||
              hasZeroMaxScore ||
              isStatusB
            );
          }
          // If user selected "Failed", show all items that simplify to "Failed"
          else if (this.statusFilter === "Failed") {
            // Don't show comment questions in Failed filter
            const isCommentQuestion =
              item.question_name &&
              item.question_name.toLowerCase().includes("comment");
            const hasZeroMaxScore =
              item.max_score_of_question === 0 ||
              item.max_score_of_question === "0" ||
              parseInt(item.max_score_of_question) === 0;
            const isStatusB = item.status === "B";

            if (isCommentQuestion || hasZeroMaxScore || isStatusB) {
              return false; // Comment questions should not appear in Failed filter
            }

            return simplifiedStatus === "Failed";
          }

          return true;
        });

        filtered.forEach((item, index) => {
          if (
            item.question_name &&
            item.question_name.toLowerCase().includes("comment")
          ) {
          }
        });
      }

      if (this.testTakenCountFilter) {
        filtered = filtered.filter(
          (item) =>
            item.test_taken_count &&
            item.test_taken_count.toString() ===
              this.testTakenCountFilter.toString()
        );
      }

      return filtered;
    },

    paginatedResults() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredResults.slice(start, end);
    },

    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },

    endIndex() {
      const end = this.startIndex + this.perPage;
      return end > this.filteredResults.length
        ? this.filteredResults.length
        : end;
    },

    hasActiveFilters() {
      return !!(
        this.reviewerNameFilter ||
        this.revieweeNameFilter ||
        this.groupNameFilter ||
        this.questionNameFilter ||
        this.statusFilter ||
        this.testTakenCountFilter
      );
    },

    activeFiltersCount() {
      let count = 0;
      if (this.reviewerNameFilter) count++;
      if (this.revieweeNameFilter) count++;
      if (this.groupNameFilter) count++;
      if (this.questionNameFilter) count++;
      if (this.statusFilter) count++;
      if (this.testTakenCountFilter) count++;
      return count;
    },

    uniqueReviewers() {
      return this.filterOptions.reviewers || [];
    },

    uniqueReviewees() {
      return this.filterOptions.reviewees || [];
    },

    uniqueGroups() {
      return this.filterOptions.sections || [];
    },

    uniqueQuestions() {
      // Extract unique question names from the flattened data with their order
      const questionsMap = new Map();

      if (this.resultData.data && this.resultData.data.length > 0) {
        this.resultData.data.forEach((group) => {
          if (group.questions && group.questions.length > 0) {
            group.questions.forEach((question) => {
              if (
                question.question_name &&
                !questionsMap.has(question.question_name)
              ) {
                questionsMap.set(question.question_name, {
                  name: question.question_name,
                  order: question.order || 0,
                });
              }
            });
          }
        });
      }

      // Sort by order field, then return just the names
      return Array.from(questionsMap.values())
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((item) => item.name);
    },

    uniqueStatuses() {
      // Simplify status options for dropdown
      const statuses = new Set();
      const originalStatuses = this.filterOptions.statuses || [];

      originalStatuses.forEach((status) => {
        if (status && status.includes("Passed")) {
          statuses.add("Passed");
        } else if (status) {
          statuses.add("Failed");
        }
      });

      return Array.from(statuses).sort();
    },

    uniqueTestCounts() {
      return this.filterOptions.test_taken_counts || [];
    },

    totalQuestions() {
      if (!this.resultData.data || this.resultData.data.length === 0) {
        return 0;
      }

      return this.resultData.data.reduce((total, group) => {
        return total + (group.questions ? group.questions.length : 0);
      }, 0);
    },

    totalReviews() {
      // Count unique reviewee names to get total reviews
      if (!this.resultData.data || this.resultData.data.length === 0) {
        return 0;
      }

      const uniqueReviewees = new Set();
      this.resultData.data.forEach((group) => {
        if (group.reviewee_name) {
          uniqueReviewees.add(group.reviewee_name);
        }
      });

      return uniqueReviewees.size;
    },
  },

  methods: {
    onFilterChange() {
      this.currentPage = 1;
      this.$emit("filter-change", {
        reviewerName: this.reviewerNameFilter,
        revieweeName: this.revieweeNameFilter,
        groupName: this.groupNameFilter,
        questionName: this.questionNameFilter,
        status: this.statusFilter,
        testTakenCount: this.testTakenCountFilter,
      });
    },

    clearAllFilters() {
      this.reviewerNameFilter = "";
      this.revieweeNameFilter = "";
      this.groupNameFilter = "";
      this.questionNameFilter = "";
      this.statusFilter = "";
      this.testTakenCountFilter = "";
      this.currentPage = 1;
      this.$emit("filter-change", {});
    },

    getStatusVariant(
      status,
      maxScore = null,
      questionName = null,
      minScore = null
    ) {
      // ÖNCELİK 1: Status kontrolü (en güvenilir) - SADECE "B" comment demek
      if (status === "B") {
        return "success";
      }

      // ÖNCELİK 2: Question name kontrolü (genişletilmiş pattern'ler)
      if (questionName) {
        const lowerName = questionName.toLowerCase();
        if (
          lowerName.includes("comment") ||
          lowerName.includes("note") ||
          lowerName.includes("enter your") ||
          lowerName.includes("please enter") ||
          lowerName.includes("regarding")
        ) {
          return "success";
        }
      }

      // ÖNCELİK 3: Score kontrolü (yedek) - null/undefined da comment olabilir
      if (
        maxScore === 0 ||
        maxScore === "0" ||
        parseInt(maxScore) === 0 ||
        maxScore === null ||
        maxScore === undefined
      ) {
        return "success"; // Yeşil
      }

      if (minScore === 0 || minScore === "0" || parseInt(minScore) === 0) {
        return "success"; // Yeşil
      }

      // If status contains "Passed" (Group Passed, Passed), show as success
      if (status && (status.includes("Passed") || status.includes("passed"))) {
        return "success";
      }
      // All other statuses (Compulsory Failed, Failed, etc.) show as danger
      return "danger";
    },

    getSimplifiedStatus(
      status,
      maxScore = null,
      questionName = null,
      minScore = null
    ) {
      // ÖNCELİK 1: Status kontrolü (en güvenilir) - SADECE "B" comment demek
      if (status === "B") {
        return "Passed";
      }

      // ÖNCELİK 2: Question name kontrolü (genişletilmiş pattern'ler)
      if (questionName) {
        const lowerName = questionName.toLowerCase();
        if (
          lowerName.includes("comment") ||
          lowerName.includes("note") ||
          lowerName.includes("enter your") ||
          lowerName.includes("please enter") ||
          lowerName.includes("regarding")
        ) {
          return "Passed";
        }
      }

      // ÖNCELİK 3: Score kontrolü (yedek) - null/undefined da comment olabilir
      if (
        maxScore === 0 ||
        maxScore === "0" ||
        parseInt(maxScore) === 0 ||
        maxScore === null ||
        maxScore === undefined
      ) {
        return "Passed";
      }

      if (minScore === 0 || minScore === "0" || parseInt(minScore) === 0) {
        return "Passed";
      }

      // Simplify status display
      if (status && status.includes("Passed")) {
        return "Passed";
      }
      return "Training Needs Identified";
    },

    // Comment soruları için failed-cell class'ını kontrol eden helper
    shouldApplyFailedStyle(item) {
      // ÖNCELİK 1: Status kontrolü (en güvenilir) - SADECE "B" comment demek
      if (item.status === "B") {
        return false;
      }

      // ÖNCELİK 2: Question name kontrolü (genişletilmiş pattern'ler)
      if (item.question_name) {
        const lowerName = item.question_name.toLowerCase();
        if (
          lowerName.includes("comment") ||
          lowerName.includes("note") ||
          lowerName.includes("enter your") ||
          lowerName.includes("please enter") ||
          lowerName.includes("regarding")
        ) {
          return false;
        }
      }

      // ÖNCELİK 3: Score kontrolü (yedek) - null/undefined da comment olabilir
      if (
        item.max_score_of_question === 0 ||
        item.max_score_of_question === "0" ||
        parseInt(item.max_score_of_question) === 0 ||
        item.max_score_of_question === null ||
        item.max_score_of_question === undefined
      ) {
        return false;
      }

      if (
        item.min_score_of_quest === 0 ||
        item.min_score_of_quest === "0" ||
        parseInt(item.min_score_of_quest) === 0
      ) {
        return false;
      }

      return item.status === "Failed";
    },

    formatDate(date) {
      if (date) {
        const parsedDate = moment(
          String(date),
          ["MM-DD-YYYY", "M-D-YYYY", "YYYY-MM-DD"],
          true
        );
        if (parsedDate.isValid()) {
          return parsedDate.format("DD/MM/YYYY");
        }
        return moment(date).format("DD/MM/YYYY");
      }
      return "";
    },

    formatCompletionTime(completionTime) {
      if (completionTime) {
        // Format timestamp to readable date-time
        const parsedDate = moment(completionTime);
        if (parsedDate.isValid()) {
          return parsedDate.format("DD/MM/YYYY HH:mm");
        }
        return completionTime;
      }
      return "Not Completed";
    },

    getRowClass(item) {
      let classes = [];

      if (item.rowType === "group") {
        classes.push("table-group-header");
      } else if (item.rowType === "question") {
        classes.push("table-question-row");
      }

      // Comment soruları için kırmızı stil uygulanmaz
      if (
        item.status === "Failed" &&
        !(
          (item.question_name &&
            item.question_name.toLowerCase().includes("comment")) ||
          item.status === "B" ||
          item.max_score_of_question === 0 ||
          item.max_score_of_question === "0" ||
          parseInt(item.max_score_of_question) === 0
        )
      ) {
        classes.push("failed-row");
      }

      return classes.join(" ");
    },

    getRowAttributes(item) {
      // Comment soruları için failed attributes uygulanmaz
      if (
        item.status === "Failed" &&
        !(
          (item.question_name &&
            item.question_name.toLowerCase().includes("comment")) ||
          item.status === "B" ||
          item.max_score_of_question === 0 ||
          item.max_score_of_question === "0" ||
          parseInt(item.max_score_of_question) === 0
        )
      ) {
        return {
          "data-status": "failed",
          class: "failed-row-global",
        };
      }
      return {};
    },

    getSuccessRatePercentage(successRate) {
      // "89.19%" formatından sayıyı çıkar
      if (!successRate) return 0;
      const numericValue = parseFloat(successRate.toString().replace("%", ""));
      return Math.min(Math.max(numericValue, 0), 100); // 0-100 arasında sınırla
    },

    getSuccessRateColor(successRate, item = null) {
      // Eğer bu individual question failed ise, genel success rate ne olursa olsun kırmızı göster
      if (item && this.shouldApplyFailedStyle(item)) {
        return "#dc3545"; // Kırmızı (failed question için)
      }

      const percentage = this.getSuccessRatePercentage(successRate);

      if (percentage >= 90) {
        return "#28a745"; // Koyu yeşil
      } else if (percentage >= 75) {
        return "#6f42c1"; // Mor
      } else if (percentage >= 50) {
        return "#ffc107"; // Sarı
      } else if (percentage >= 25) {
        return "#fd7e14"; // Turuncu
      } else {
        return "#dc3545"; // Kırmızı
      }
    },

    // Comment sorularını kontrol eden helper metod
    isCommentQuestion(item) {
      // ÖNCELİK 1: Status kontrolü (en güvenilir) - SADECE "B" comment demek
      if (item.status === "B") {
        return true;
      }

      // ÖNCELİK 2: Question name kontrolü (genişletilmiş pattern'ler)
      if (item.question_name) {
        const lowerName = item.question_name.toLowerCase();
        if (
          lowerName.includes("comment") ||
          lowerName.includes("note") ||
          lowerName.includes("enter your") ||
          lowerName.includes("please enter") ||
          lowerName.includes("regarding")
        ) {
          return true;
        }
      }

      // ÖNCELİK 3: Max Score = 0 veya null kontrolü (comment questions have no score)
      if (
        item.max_score_of_question === 0 ||
        item.max_score_of_question === "0" ||
        item.max_score_of_question === null ||
        item.max_score_of_question === undefined
      ) {
        return true;
      }

      return false;
    },

    // Min Score display için özel metod
    getMinScoreDisplay(item) {
      // Comment soruları için "Comment" yazısı göster
      if (this.isCommentQuestion(item)) {
        return "Comment";
      }

      // Comment olmayan ama min score 0 olan sorular için "0" göster
      if (
        item.min_score_of_quest === 0 ||
        item.min_score_of_quest === "0" ||
        parseInt(item.min_score_of_quest) === 0
      ) {
        return "0";
      }

      // Eğer değer null/undefined ise ve comment soru değilse boş göster
      if (
        item.min_score_of_quest === null ||
        item.min_score_of_quest === undefined
      ) {
        return "-";
      }

      // Normal durumlarda değeri olduğu gibi göster
      return item.min_score_of_quest;
    },
  },
};
</script>

<style lang="scss" scoped>
// Import the same styles from the original file
@import "@core/scss/vue/libs/vue-select.scss";

.summary-report-tab {
  // Use the same styling as the original component
  .filter-card {
    border: 1px solid #e3e6f0;
    box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
    border-radius: 0.35rem;

    &.compact {
      .card-body {
        padding: 0.5rem 1rem;
      }
    }

    .form-group {
      margin-bottom: 0.25rem;

      label {
        font-weight: 500;
        color: #5a5c69;
        font-size: 0.75rem;
        margin-bottom: 0.25rem;
      }
    }

    .v-select.form-select-sm {
      .vs__dropdown-toggle {
        border: 1px solid #d1d3e2;
        border-radius: 0.25rem;
        min-height: 32px;

        &:hover {
          border-color: #a0a0a0;
        }

        &.vs--open {
          border-color: #80bdff;
          box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.25);
        }
      }

      .vs__selected-options {
        padding: 2px 8px;
      }

      .vs__search {
        font-size: 0.8rem;
        padding: 0;
        margin: 0;
      }

      .vs__selected {
        font-size: 0.8rem;
        color: #5a5c69;
        padding: 0;
        margin: 0;
      }

      .vs__actions {
        padding: 2px 8px;
      }
    }

    .btn-outline-warning {
      border-color: #f6c23e;
      color: #f6c23e;
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;

      &:hover {
        background-color: #f6c23e;
        border-color: #f6c23e;
        color: #fff;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  // Table styles (same as original)
  // Excel-style table headers
  .excel-header {
    background-color: #f8f9fa !important;
    border: 1px solid #dee2e6 !important;
    font-weight: 600 !important;
    font-size: 11px !important;
    padding: 4px 6px !important;
    text-align: center !important;
    color: #495057 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    vertical-align: middle !important;
  }

  .compact-table-container {
    width: 100%;
    overflow-x: auto;

    .results-table-wrapper {
      background: white;
      border-radius: 0.375rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      width: 100%;

      .table-responsive {
        overflow-x: auto !important;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
          height: 8px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;

          &:hover {
            background: #a1a1a1;
          }
        }
      }

      .horizontal-scroll {
        overflow-x: auto !important;
        overflow-y: visible !important;
        white-space: nowrap !important;

        .table {
          min-width: max-content !important;
          white-space: nowrap !important;

          th,
          td {
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
        }
      }
    }

    .compact-results-table {
      font-size: 0.875rem;
      border-collapse: collapse;
      border: 1px solid #dee2e6;
      table-layout: auto; // Allow dynamic column widths
      width: 100%;
      min-width: max-content;

      thead th {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-bottom: 2px solid #dee2e6;
        border-right: 1px solid #dee2e6;
        font-weight: 600;
        font-size: 0.8rem;
        padding: 0.75rem 0.5rem;
        vertical-align: middle;
        min-width: 100px !important;
        max-width: 400px !important;
        width: auto !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        white-space: normal !important;
        word-break: normal !important;
      }

      tbody td {
        padding: 0.6rem 0.5rem;
        vertical-align: middle;
        border-top: 1px solid #dee2e6;
        border-right: 1px solid #dee2e6;
        border-bottom: 1px solid #f8f9fa;
        min-width: 100px !important;
        max-width: 400px !important;
        width: auto !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        white-space: normal !important;
        word-break: normal !important;
      }

      tbody tr:hover {
        background-color: rgba(0, 123, 255, 0.04);
      }

      tbody tr.table-group-header {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-top: 2px solid #007bff;
        font-weight: 600;

        &:hover {
          background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
        }

        td {
          padding: 0.8rem 0.5rem;
          border-top: 2px solid #007bff;
          font-weight: 600;
        }
      }

      tbody tr.table-question-row {
        background-color: #fdfdfd;

        &:hover {
          background-color: rgba(0, 123, 255, 0.06);
        }

        td {
          padding: 0.5rem 0.5rem;
          border-top: 1px solid #dee2e6;
          border-right: 1px solid #dee2e6;
          border-bottom: 1px solid #f1f1f1;
          border-left: 3px solid #e9ecef;
          max-width: 300px !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
          word-break: break-word !important;
        }
      }

      // Failed row global styling
      tbody tr.failed-row,
      tbody tr.failed-row-global,
      tbody tr[data-status="failed"] {
        background-color: rgba(220, 53, 69, 0.05) !important;
        border-left: 4px solid #dc3545 !important;

        &:hover {
          background-color: rgba(220, 53, 69, 0.1) !important;
        }

        td {
          color: #dc3545 !important;
          border-color: rgba(220, 53, 69, 0.2) !important;

          * {
            color: #dc3545 !important;
          }

          .badge {
            border-color: #dc3545 !important;
            background-color: #dc3545 !important;
            color: white !important;
          }

          .score-badge {
            background-color: #dc3545 !important;
            color: white !important;
          }

          .passing-score-badge {
            color: #dc3545 !important;
            border: 1px solid #dc3545 !important;
          }

          .test-count-badge {
            background-color: rgba(220, 53, 69, 0.1) !important;
            color: #dc3545 !important;
            border: 1px solid #dc3545 !important;
          }

          .success-rate-text {
            color: #dc3545 !important;
            font-weight: 600 !important;
          }

          .completion-date {
            color: #dc3545 !important;
          }

          .comment-text,
          .event-name-text {
            color: #dc3545 !important;
          }
        }
      }
    }

    // Failed cell specific styling
    .failed-cell {
      color: #dc3545 !important;

      border-radius: 3px !important;
      font-weight: 600 !important;
    }

    .question-name-cell {
      &.group-header {
        .group-text {
          color: #495057;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
        }
      }

      &.question-row {
        .question-indent {
          position: relative;
        }
      }

      &.failed-row {
        color: #dc3545 !important;

        * {
          color: #dc3545 !important;
        }

        .badge {
          border-color: #dc3545 !important;
        }
      }

      .question-text {
        display: block;
        max-width: 300px;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: 1.4;
        font-weight: 500;
      }
    }

    .score-badge {
      display: inline-block;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      font-weight: 600;
      font-size: 0.875rem;
      text-align: center;
      max-width: 300px;
      word-wrap: break-word;
      overflow-wrap: break-word;

      &.group-score {
        font-size: 0.9rem;
        font-weight: 700;
        padding: 0.3rem 0.6rem;
        background: linear-gradient(
          135deg,
          #28a745 0%,
          #20c997 100%
        ) !important;
      }
    }

    .passing-score-badge {
      display: inline-block;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      background-color: #e9ecef;
      color: #495057;
      font-size: 0.8rem;
      font-weight: 500;

      &.font-weight-bold {
        font-weight: 700;
        background-color: #ffc107;
        color: #212529;
      }
    }

    .status-badge {
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.35rem 0.6rem;

      &.group-status {
        font-size: 0.8rem;
        padding: 0.4rem 0.7rem;
        font-weight: 700;
      }
    }

    .status-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      align-items: center;
      justify-content: center;

      .compulsory-badge {
        font-size: 0.65rem;
        font-weight: 600;
        padding: 0.25rem 0.4rem;
        background-color: #ffc107 !important;
        color: #212529 !important;
        border: 1px solid #e0a800;
        white-space: nowrap;
      }
    }

    .score-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      align-items: center;
      justify-content: center;
    }

    .success-rate-cell {
      .success-rate-progress-container {
        position: relative;
        width: 100%;
        height: 24px;
        background-color: #e9ecef;
        border-radius: 4px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .success-rate-progress-bar {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        transition: width 0.3s ease;
        border-radius: 4px;
        opacity: 0.8;
      }

      .success-rate-text {
        position: relative;
        z-index: 2;
        font-weight: 600;
        color: #212529;
        text-shadow: 0 0 3px rgba(255, 255, 255, 0.8);
        font-size: 0.75rem;
      }
    }

    .test-count-badge {
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.25rem 0.5rem;
    }

    .completion-date {
      font-size: 0.8rem;
      color: #6c757d;
      font-weight: 500;
    }

    .event-name-text {
      max-width: 300px;
      word-wrap: break-word;
      overflow-wrap: break-word;
      display: block;
    }

    .comment-indicator {
      background-color: #e3f2fd !important;
      color: #1976d2 !important;
      font-style: italic;
      font-weight: 600;
      padding: 2px 6px;
      border-radius: 4px;
      border: 1px solid #bbdefb;
    }
  }
}

// Pagination styling
.pagination {
  .page-link {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
    border-color: #d1d3e2;
    color: #5a5c69;

    &:hover {
      background-color: #f8f9fa;
      border-color: #d1d3e2;
      color: #5a5c69;
    }
  }

  .page-item.active .page-link {
    background-color: #007bff;
    border-color: #007bff;
  }

  .page-item.disabled .page-link {
    background-color: #f8f9fa;
    border-color: #d1d3e2;
    color: #6c757d;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .filter-card {
    .card-body {
      padding: 1rem 0.75rem;
    }
  }

  .compact-results-table {
    // Mobile-specific overrides
    thead th,
    tbody td {
      padding: 3px 6px;
      font-size: 11px;
    }

    .question-name-cell .question-text {
      max-width: 200px;
    }
  }
}

// Disabled filter styling
.v-select.vs--disabled {
  opacity: 0.6;

  .vs__dropdown-toggle {
    background-color: #f8f9fa !important;
    cursor: not-allowed;
  }

  .vs__selected-options {
    color: #6c757d !important;
  }

  .vs__search {
    cursor: not-allowed;
  }
}

// Force wrapping with max-width control - Override Bootstrap
.compact-results-table {
  td,
  th {
    max-width: 300px !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
    word-break: break-word !important;

    * {
      max-width: 300px !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      word-break: break-word !important;
    }
  }
}

// Specific Bootstrap-Vue table override
.table td,
.table th {
  max-width: 200px !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  white-space: normal !important;
  word-break: break-word !important;
}

// Custom table class for word breaking
.word-break-table {
  table-layout: auto !important;

  td,
  th {
    max-width: 300px !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
    word-break: break-word !important;
    hyphens: auto !important;
  }
}

// Ultra-strong Bootstrap-Vue override
.summary-report-tab
  .table-responsive
  .table.compact-results-table.text-break.word-break-table {
  table-layout: auto !important;

  thead th,
  tbody td,
  tfoot td {
    min-width: 100px !important;
    max-width: 400px !important;
    width: auto !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
    word-break: normal !important;
    hyphens: auto !important;
  }
}
</style>
